/**
 * AIReviewDialog · iter171 (2026-02-27)
 *
 * EU-AI-Act-Konformität (Art. 50 · Transparenzpflicht):
 * Zeigt dem User den KI-generierten Vorschlag und verlangt eine EXPLIZITE
 * redaktionelle Freigabe, bevor der Text in das Formular übernommen wird.
 *
 * Siehe: https://digital-strategy.ec.europa.eu/de/policies/eu-icons-labelling-ai-generated-content
 *
 * Props:
 *   open        : boolean
 *   originalText: aktueller Feldinhalt (Vergleich)
 *   generated   : KI-Vorschlag
 *   action      : Label (z.B. „Generieren", „Verbessern")
 *   onAccept    : (text) => void  — Bestätigung
 *   onReject    : () => void      — Ablehnung / Schließen
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, CheckCircle2, AlertTriangle, Sparkles } from "lucide-react";

export default function AIReviewDialog({
  open,
  originalText = "",
  generated = "",
  action = "",
  onAccept,
  onReject,
}) {
  const { t } = useTranslation();
  const [confirmed, setConfirmed] = useState(false);

  // Reset on open
  useEffect(() => {
    if (open) setConfirmed(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onReject && onReject(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onReject]);

  if (!open) return null;

  return (
    <div
      data-testid="ai-review-dialog"
      className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
      onClick={onReject}
    >
      <div
        className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white flex items-center justify-between px-5 py-3 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-amber-600" />
            <h2 className="text-sm font-bold text-[#0A0A0A]">
              KI-Vorschlag prüfen{action ? ` · ${action}` : ""}
            </h2>
          </div>
          <button
            data-testid="ai-review-close"
            onClick={onReject}
            className="p-1.5 -mr-1.5 rounded-full hover:bg-[#F5F5F5]"
            aria-label={t("common.close_aria")}
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* EU AI-Act Hinweis */}
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-700 mt-0.5 shrink-0" />
            <p className="text-[11px] text-amber-900 leading-snug">
              Dieser Text wurde mit KI erstellt (EU AI Act Art. 50 · Transparenzpflicht).
              Prüfe ihn sorgfältig auf Richtigkeit, Angemessenheit und rechtliche Zulässigkeit,
              bevor du ihn übernimmst. Du trägst die redaktionelle Verantwortung für die
              Veröffentlichung.
            </p>
          </div>

          {/* Diff (Original vs Vorschlag) */}
          {originalText?.trim() && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-1">
                Aktuell im Feld
              </p>
              <div
                data-testid="ai-review-original"
                className="rounded-xl border border-[#E5E5E5] bg-[#FAFAFA] p-3 text-xs text-[#666] max-h-32 overflow-y-auto whitespace-pre-wrap"
              >
                {stripHtml(originalText)}
              </div>
            </div>
          )}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1">
              KI-Vorschlag
            </p>
            <div
              data-testid="ai-review-generated"
              className="rounded-xl border border-amber-300 bg-white p-3 text-sm text-[#0A0A0A] max-h-64 overflow-y-auto whitespace-pre-wrap"
            >
              {stripHtml(generated) || <span className="text-[#999] italic">{t("common.empty_placeholder")}</span>}
            </div>
          </div>

          {/* Bestätigung */}
          <label
            htmlFor="ai-review-confirm"
            className="flex items-start gap-2 p-3 rounded-xl border border-[#E5E5E5] hover:bg-[#FAFAFA] cursor-pointer"
          >
            <input
              id="ai-review-confirm"
              data-testid="ai-review-confirm-checkbox"
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-[#999] accent-[#0A0A0A] cursor-pointer"
            />
            <span className="text-xs text-[#0A0A0A] leading-snug select-none">
              Ich habe den Text <strong>{t("common.checked")}</strong>, einer <strong>Kontrolle unterzogen</strong>{" "}
              und trage die <strong>redaktionelle Verantwortung</strong> für die Veröffentlichung
              dieses Inhalts.
            </span>
          </label>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              data-testid="ai-review-reject"
              onClick={onReject}
              className="h-10 px-4 rounded-full border border-[#E5E5E5] text-sm text-[#666] hover:bg-[#F5F5F5] transition"
            >
              Verwerfen
            </button>
            <button
              data-testid="ai-review-accept"
              disabled={!confirmed || !generated}
              onClick={() => onAccept && onAccept(generated)}
              className="h-10 px-5 rounded-full bg-[#0A0A0A] text-white text-sm font-medium hover:bg-[#222] transition disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              <CheckCircle2 size={14} /> Übernehmen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function stripHtml(v) {
  if (!v) return "";
  return String(v)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .trim();
}
